import React from "react";
import styled, { css } from "styled-components";
import { transparentize } from "polished";
import { useUserSettings } from "components/Site/Contexts/UserSettings";
import { ScriptDirection } from "api/typedefs/Bible";
import { Theme } from "typedefs-ui/Theme";

interface ScriptureContainerProps {
  id?: string;
  className?: string;
  scriptDirection?: ScriptDirection;
  highlightSelectedVerse?: boolean;
  disableFootnotes?: boolean;
  theme?: Theme;
}
interface StyledScriptureContainerProps {
  fontSize: string | number;
  scriptDirection?: ScriptDirection;
  highlightSelectedVerse?: boolean;
  showVerseNumbers: boolean;
  footnotes: boolean;
}
const StyledScriptureContainer = styled.div<StyledScriptureContainerProps>`
  direction: ${({ scriptDirection = "LTR" }) => scriptDirection};
  padding-top: 6px;
  position: relative;
  color: ${props => props.theme.primaryText} !important;


  /* allows modification of font-size */
  font-size: ${({ fontSize }) => fontSize}px !important;

  sup[class^="v"] {
    color: ${props => props.theme.primaryText} !important;
  }

  ${({
    highlightSelectedVerse,
    theme: { hightlightColor, selectedColor, matchedColor }
  }) =>
    highlightSelectedVerse &&
    css`
      .verse-span {
        position: relative;
        cursor: pointer;
        transition: all 0.2s ease;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-tap-highlight-color: transparent; /* For some Androids */
        text-decoration-line: underline;
        text-decoration-style: solid;
        text-decoration-thickness: 2px;
        text-decoration-color: transparent;
        text-decoration-skip-ink: auto;
        text-underline-offset: 3px;
      }
      .verse-span.highlighted {
        text-decoration-color: ${hightlightColor};
      }
      .verse-span.selected {
        text-decoration-color: ${selectedColor};
        background-color: ${transparentize(0.9, selectedColor)};
      }
      .verse-span.feedbacked {
        text-decoration-color: #0c68f1;
      }

      .verse-span.matched {
        animation: target-fade 5s 1;
      }

      @keyframes target-fade {
        0% {
          background-color: ${transparentize(1, matchedColor)};
        }
        50% {
          background-color: ${transparentize(0.8, matchedColor)};
        }
        100% {
          background-color: ${transparentize(1, matchedColor)};
        }
      }
    `}

  /* toggles display of verse numbers */
  ${({ showVerseNumbers }) =>
    !showVerseNumbers &&
    css`
      p .v {
        display: none;
      }
    `} 

  /* toggles display of footnootes */
  ${({ footnotes }) =>
    footnotes
      ? css`
          p .x,
          p .f {
            float: none;
            display: inline-block;
            clear: none;
            width: auto;
            vertical-align: super;
            margin: 0;
            text-align: left;
            border-left: none;
            border-right: none;
            padding: 0;
            text-indent: 0;
            background-color: transparent;
            outline: none;
          }
        `
      : css`
          p .x,
          p .f {
            display: none;
          }
        `};
`;

const ScriptureContainer: React.FC<ScriptureContainerProps> = ({
  disableFootnotes,
  ...props
}) => {
  const { fontSize, showVerseNumbers, showFootnotes } = useUserSettings();
  const shouldShowFootnotes = disableFootnotes ? false : showFootnotes;

  return (
    <StyledScriptureContainer
      {...props}
      footnotes={shouldShowFootnotes}
      showVerseNumbers={showVerseNumbers}
      fontSize={fontSize}
    />
  );
};

export default ScriptureContainer;
